import * as React from 'react';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { connect } from 'react-redux';
import { fetchUserInfoIfNeeded, hasPermission } from '@pec/aion-ui-core/actions/userInfo';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { UserInfoActivitiesType } from '@pec/aion-ui-core/interfaces/userInfo';
import { CommonRootState } from '@pec/aion-ui-core/build/combineReducers';
import { RootActions } from '@pec/aion-ui-core/build/combineActions';

const { Audit, Manage, Write } = ActivityAction;
const { TaskGroups, OrganizationAssets } = ActivityResourceName;

type RouteParams = {
  organizationId: string;
};

type IChildrenProps = {
  hasGlobalPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  hasOrganizationPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  hasOrganizationAssetPermission: () => boolean;
  hasTaskGroupsWritePermission: () => boolean;
  hasTaskGroupsManagePermission: () => boolean;
  hasTaskGroupsAuditPermission: () => boolean;
};

type OwnProps = {
  children: (props: IChildrenProps) => JSX.Element;
};

const mapStateToProps = (state: CommonRootState) => state.userInfo;

const mapDispatchToProps = (dispatch: ThunkDispatch<CommonRootState, null, RootActions>) => ({
  fetchUserInfoIfNeeded: () => dispatch(fetchUserInfoIfNeeded())
});

type Props = OwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  RouteComponentProps<RouteParams>;

class HasPermission extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    props.fetchUserInfoIfNeeded();
  }

  hasGlobalPermission = (action: ActivityAction, resourceName: ActivityResourceName) =>
    hasPermission(this.props.userInfo, action, resourceName);

  hasOrganizationPermission = (action: ActivityAction, resourceName: ActivityResourceName) => {
    const {
      userInfo,
      match: {
        params: { organizationId }
      }
    } = this.props;

    return organizationId
      ? hasPermission(userInfo, action, resourceName, [
          {
            type: UserInfoActivitiesType.Organization,
            id: organizationId
          }
        ])
      : false;
  };

  hasTaskGroupsWritePermission = () =>
    this.hasGlobalPermission(Write, TaskGroups) || this.hasOrganizationPermission(Write, TaskGroups);

  hasOrganizationAssetPermission = () =>
    this.hasGlobalPermission(Manage, OrganizationAssets) || this.hasOrganizationPermission(Manage, OrganizationAssets);

  hasTaskGroupsManagePermission = () =>
    this.hasGlobalPermission(Manage, TaskGroups) || this.hasOrganizationPermission(Manage, TaskGroups);

  hasTaskGroupsAuditPermission = () =>
    this.hasGlobalPermission(Audit, TaskGroups) || this.hasOrganizationPermission(Audit, TaskGroups);

  render() {
    return this.props.children({
      hasGlobalPermission: this.hasGlobalPermission,
      hasOrganizationPermission: this.hasOrganizationPermission,
      hasTaskGroupsWritePermission: this.hasTaskGroupsWritePermission,
      hasTaskGroupsManagePermission: this.hasTaskGroupsManagePermission,
      hasTaskGroupsAuditPermission: this.hasTaskGroupsAuditPermission,

      hasOrganizationAssetPermission: this.hasOrganizationAssetPermission
    });
  }
}

export const HasPermissionContainer = withRouter(connect(mapStateToProps, mapDispatchToProps)(HasPermission));
